import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API_ENDPOINTS } from "../../api/config";
import EmpLayout from "./emp_layout";
import "./css/post_job.css"; 
import "./css/job-details.css"; 

const STATUS_OPTIONS = [
  { key: "Applied", label: "Applied", color: "#3b82f6", bg: "#eff6ff" },
  { key: "UnderReview", label: "Under Review", color: "#f59e0b", bg: "#fef3c7" },
  { key: "Shortlisted", label: "Shortlisted", color: "#10b981", bg: "#ecfdf5" },
  { key: "InterviewScheduled", label: "Interview Scheduled", color: "#8b5cf6", bg: "#f5f3ff" },
  { key: "Offered", label: "Offered", color: "#ec4899", bg: "#fdf2f8" },
  { key: "Rejected", label: "Rejected", color: "#ef4444", bg: "#fef2f2" },
  { key: "Withdrawn", label: "Withdrawn", color: "#6b7280", bg: "#f3f4f6" }
];

export function JobDetailsView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dropdownRef = useRef(null); 
  const statusPanelRef = useRef(null); 
  
  const [activeTab, setActiveTab] = useState("edit"); 
  const [isEditing, setIsEditing] = useState(false);  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [showReopenConfirm, setShowReopenConfirm] = useState(false);
  const [filterStatus, setFilterStatus] = useState("All");
  
  const [countiesList, setCountiesList] = useState([]);
  const [globalSkillsPool, setGlobalSkillsPool] = useState([]); 
  const [dbSpecs, setDbSpecs] = useState([]);
  
  const [skillInput, setSkillInput] = useState("");
  const [showSkillDropdown, setShowSkillDropdown] = useState(false);
  const [applicants, setApplicants] = useState([]);
  const [metaStats, setMetaStats] = useState({ viewCount: 0, applicationCount: 0 });
  
  const [form, setForm] = useState({
    title: "", description: "", type: "Full-time", spec: "", city: "",
    salaryMin: "", salaryMax: "", salaryCurrency: "KSh", salaryPer: "month",
    experience: "", deadline: "", skills: [], status: ""
  });
  
  const [originalForm, setOriginalForm] = useState({});

  useEffect(() => {
    const bootstrapData = async () => {
      const token = localStorage.getItem("token");
      const headers = { "Authorization": `Bearer ${token}` };
      
      try {
        const [countyRes, skillsRes, specRes] = await Promise.all([
          fetch(`${API_ENDPOINTS.DIMENSIONS}/counties`, { headers }),
          fetch(`${API_ENDPOINTS.DIMENSIONS}/skills-pool`, { headers }),
          fetch(`${API_ENDPOINTS.DIMENSIONS}/specialisations`, { headers })
        ]);

        if (countyRes.ok) setCountiesList(await countyRes.json());
        if (skillsRes.ok) setGlobalSkillsPool(await skillsRes.json());
        if (specRes.ok) setDbSpecs(await specRes.json());

        const res = await fetch(`${API_ENDPOINTS.EMPJOBDETAILS}/${id}`, { headers });
        if (res.ok) {
          const data = await res.json();
          
          const initialFormState = {
            title: data.title || "",
            description: data.description || "",
            type: data.jobType || "Full-time",
            spec: data.specialisation || "",
            city: data.city || "",
            salaryMin: data.salaryMin || "",
            salaryMax: data.salaryMax || "",
            salaryCurrency: data.salaryCurrency || "KSh",
            salaryPer: data.salaryPer || "month",
            experience: data.experienceRequired || "",
            deadline: data.applicationDeadline ? data.applicationDeadline.split("T")[0] : "",
            skills: data.skills || [],
            status: data.status || "Active"
          };

          setForm(initialFormState);
          setOriginalForm(initialFormState);
          setMetaStats({ viewCount: data.viewCount || 0, applicationCount: data.applicationCount || 0 });
          setApplicants(data.applicants || []);
        }
      } catch (err) {
        console.error("Failed executing management bootstrap sequence", err);
      } finally {
        setLoading(false);
      }
    };
    bootstrapData();
  }, [id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowSkillDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  
  const handleSelectSkill = (skillName) => {
    if (skillName && !form.skills.includes(skillName)) {
      setForm(f => ({ ...f, skills: [...f.skills, skillName] }));
    }
    setSkillInput("");
    setShowSkillDropdown(false);
  };

  const removeSkill = (s, e) => {
    if (e) e.preventDefault();
    setForm(f => ({ ...f, skills: f.skills.filter(x => x !== s) }));
  };

  const filteredSkillsPool = globalSkillsPool.filter(pooledSkill => {
    const nameString = typeof pooledSkill === 'string' ? pooledSkill : pooledSkill.name || "";
    return nameString.toLowerCase().includes(skillInput.toLowerCase()) && !form.skills.includes(nameString);
  });

  const handleCancel = () => {
    setForm(originalForm);
    setSkillInput("");
    setShowSkillDropdown(false);
    setShowReopenConfirm(false);
    setIsEditing(false);
  };

  const handleUpdateJobClick = () => {
    if (!form.title || !form.description || !form.spec || !form.city) {
      alert("Please fill in all required fields marked with *");
      return;
    }

    if (form.deadline) {
      const selectedDeadline = new Date(form.deadline);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDeadline < today) {
        alert("The modification cannot be accepted because the application deadline is set in the past.");
        return;
      }
    } else if (originalForm.status?.toLowerCase() === "closed") {
      alert("Please specify a future application deadline date to reopen this closed job listing.");
      return;
    }

    if (originalForm.status?.toLowerCase() === "closed") {
      setShowReopenConfirm(true);
      statusPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    executeSaveRequest(form.status);
  };

  const executeSaveRequest = async (finalStatus) => {
    setSaving(true);
    const token = localStorage.getItem("token");

    const payload = {
      Title: form.title,
      Description: form.description,
      Specialisation: form.spec,
      JobType: form.type,
      City: form.city,
      SalaryMin: form.salaryMin ? parseFloat(form.salaryMin) : null,
      SalaryMax: form.salaryMax ? parseFloat(form.salaryMax) : null,
      SalaryCurrency: form.salaryCurrency,
      SalaryPer: form.salaryPer,
      ExperienceRequired: form.experience,
      ApplicationDeadline: form.deadline ? form.deadline : null,
      Skills: form.skills,
      Status: finalStatus 
    };

    try {
      const res = await fetch(`${API_ENDPOINTS.EMPUPDATELISTING}/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        alert("Listing changes saved successfully!");
        const updatedState = { ...form, status: finalStatus };
        setForm(updatedState);
        setOriginalForm(updatedState); 
        setIsEditing(false);           
        setShowReopenConfirm(false);
      } else {
        alert("Failed to update listing. Please verify input variables.");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // Compute filtered array in real-time based on selected pipeline stage
  const displayedApplicants = applicants.filter(a => {
    if (filterStatus === "All") return true;
    
    // Normalize string key comparison spaces
    const candidateStatus = (a.currentStatus || "UnderReview").replace(/\s+/g, '');
    const cleanFilter = filterStatus.replace(/\s+/g, '');
    
    // Map internal pipeline logic tracking
    if (cleanFilter === "Interviewing") {
      return candidateStatus.toLowerCase() === "interviewscheduled";
    }
    
    return candidateStatus.toLowerCase() === cleanFilter.toLowerCase();
  });

  if (loading) return <EmpLayout title="Manage" titleEm="Job"><div className="ml-loading">Loading management details matrix...</div></EmpLayout>;

  return (
    <EmpLayout title="Manage" titleEm="Job">
      <div className="jd-header-nav">
        <button type="button" className="emp-btn-ghost" onClick={() => navigate("/employer/listings")}>← Back to Listings</button>
        <div className="jd-tab-cluster">
          <button type="button" className={`jd-tab-btn ${activeTab === "edit" ? "active" : ""}`} onClick={() => { setActiveTab("edit"); }}>Job Specification</button>
          <button type="button" className={`jd-tab-btn ${activeTab === "applicants" ? "active" : ""}`} onClick={() => { setActiveTab("edit"); setIsEditing(false); setActiveTab("applicants"); }}>
            Applicants ({applicants.length})
          </button>
        </div>
      </div>

      {activeTab === "edit" ? (
        <div className="pj-layout" style={{ marginTop: "1.5rem" }}>
          <div>
            {/* Top Operational Action Control Bar */}
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "1rem" }}>
              {!isEditing ? (
                <button type="button" className="emp-btn-primary" style={{ background: "#4f46e5" }} onClick={() => setIsEditing(true)}>
                  ✏️ Edit Details
                </button>
              ) : (
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <button type="button" className="emp-btn-ghost" onClick={handleCancel} disabled={saving || showReopenConfirm}>Cancel</button>
                  <button type="button" className="emp-btn-primary" onClick={handleUpdateJobClick} disabled={saving || showReopenConfirm}>
                    {saving ? "Saving Changes..." : "💾 Save Changes"}
                  </button>
                </div>
              )}
            </div>

            {/* Basic Info Module */}
            <div className="pj-card" style={{ opacity: showReopenConfirm ? 0.6 : 1, transition: "opacity 0.25s ease" }}>
              <div className="pj-card-title">Basic <em>information</em></div>
              <div className="emp-field">
                <label className="emp-label">Job Title *</label>
                {isEditing ? (
                  <input className="emp-input" value={form.title} onChange={set("title")} placeholder="e.g. Senior Hair Stylist" disabled={showReopenConfirm} />
                ) : (
                  <div className="jd-view-text" style={{ fontWeight: "600", fontSize: "1.15rem", color: "#111827" }}>{form.title || "—"}</div>
                )}
              </div>
              <div className="emp-field">
                <label className="emp-label">Job Description *</label>
                {isEditing ? (
                  <textarea className="emp-textarea" rows={6} value={form.description} onChange={set("description")} style={{ minHeight: 140 }} placeholder="Describe duties..." disabled={showReopenConfirm} />
                ) : (
                  <div className="jd-view-text" style={{ whiteSpace: "pre-wrap", color: "#374151", lineHeight: "1.6" }}>{form.description || "—"}</div>
                )}
              </div>
              <div className="emp-grid2">
                <div className="emp-field">
                  <label className="emp-label">Specialisation *</label>
                  {isEditing ? (
                    <select className="emp-select" value={form.spec} onChange={set("spec")} disabled={showReopenConfirm}>
                      <option value="">Select specialisation</option>
                      {globalSkillsPool.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  ) : (
                    <div className="jd-view-text" style={{ color: "#111827" }}>{form.spec}</div>
                  )}
                </div>
                <div className="emp-field">
                  <label className="emp-label">Job Type</label>
                  {isEditing ? (
                    <select className="emp-select" value={form.type} onChange={set("type")} disabled={showReopenConfirm}>
                      {["Full-time", "Part-time", "Freelance", "Contract"].map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  ) : (
                    <div className="jd-view-text" style={{ color: "#111827" }}>{form.type}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Location & Salary Module */}
            <div className="pj-card" style={{ opacity: showReopenConfirm ? 0.6 : 1, transition: "opacity 0.25s ease" }}>
              <div className="pj-card-title">Location & <em>salary</em></div>
              <div className="emp-field">
                <label className="emp-label">City / Location *</label>
                {isEditing ? (
                  <select className="emp-select" value={form.city} onChange={set("city")} disabled={showReopenConfirm}>
                    <option value="">Select your Location...</option>
                    {countiesList.map(county => (
                      <option key={county.id} value={county.name}>{county.name}</option>
                    ))}
                  </select>
                ) : (
                  <div className="jd-view-text" style={{ color: "#111827" }}>📍 {form.city || "—"}</div>
                )}
              </div>
              <div className="emp-field">
                <label className="emp-label">Salary Range</label>
                {isEditing ? (
                  <div className="emp-grid2" style={{ gap: "0.5rem" }}>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      <select className="emp-select" style={{ width: 90 }} value={form.salaryCurrency} onChange={set("salaryCurrency")} disabled={showReopenConfirm}>
                        <option value="KSh">KSh</option>
                        <option value="USD">USD</option>
                      </select>
                      <input className="emp-input" type="number" value={form.salaryMin} onChange={set("salaryMin")} placeholder="Min" disabled={showReopenConfirm} />
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      <input className="emp-input" type="number" value={form.salaryMax} onChange={set("salaryMax")} placeholder="Max" disabled={showReopenConfirm} />
                      <select className="emp-select" style={{ width: 110 }} value={form.salaryPer} onChange={set("salaryPer")} disabled={showReopenConfirm}>
                        <option value="month">/ month</option>
                        <option value="day">/ day</option>
                        <option value="hour">/ hour</option>
                      </select>
                    </div>
                  </div>
                ) : (
                  <div className="jd-view-text" style={{ color: "#111827", fontWeight: "500" }}>
                    {form.salaryMin || form.salaryMax ? (
                      `${form.salaryCurrency} ${Number(form.salaryMin).toLocaleString()} — ${Number(form.salaryMax).toLocaleString()} / ${form.salaryPer}`
                    ) : (
                      "Negotiable / Confidential"
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Requirements & Skills Module */}
            <div className="pj-card" style={{ opacity: showReopenConfirm ? 0.6 : 1, transition: "opacity 0.25s ease" }}>
              <div className="pj-card-title">Requirements & <em>skills</em></div>
              <div className="emp-grid2">
                <div className="emp-field">
                  <label className="emp-label">Experience Required</label>
                  {isEditing ? (
                    <select className="emp-select" value={form.experience} onChange={set("experience")} disabled={showReopenConfirm}>
                      <option value="">Any level</option>
                      {["Entry level (0–1 yr)", "Junior (1–3 yrs)", "Mid-level (3–5 yrs)", "Senior (5+ yrs)", "Management"].map(e => <option key={e} value={e}>{e}</option>)}
                    </select>
                  ) : (
                    <div className="jd-view-text" style={{ color: "#111827" }}>{form.experience || "Any experience level"}</div>
                  )}
                </div>
                <div className="emp-field">
                  <label className="emp-label">Application Deadline</label>
                  {isEditing ? (
                    <input className="emp-input" type="date" value={form.deadline} onChange={set("deadline")} disabled={showReopenConfirm} />
                  ) : (
                    <div className="jd-view-text" style={{ color: "#111827" }}>⏰ {form.deadline || "No deadline limit"}</div>
                  )}
                </div>
              </div>
              
              <div className="emp-field" style={{ marginBottom: 0 }}>
                <label className="emp-label">Required Skills</label>
                <div className="pj-skills-wrap" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: form.skills.length > 0 ? '0.75rem' : '0' }}>
                  {form.skills.map(s => (
                    <span key={s} className="pj-skill-tag" style={{ paddingRight: !isEditing ? "0.75rem" : "" }}>
                      {s} {isEditing && !showReopenConfirm && <button type="button" className="pj-skill-remove" onClick={(e) => removeSkill(s, e)}>✕</button>}
                    </span>
                  ))}
                </div>
                
                {isEditing && (
                  <div ref={dropdownRef} style={{ position: 'relative' }}>
                    <div className="pj-skill-input-row" style={{ display: "flex", gap: "0.5rem" }}>
                      <input 
                        className="emp-input" 
                        placeholder="Type to search and add skills..." 
                        value={skillInput} 
                        disabled={showReopenConfirm}
                        onChange={e => {
                          setSkillInput(e.target.value);
                          setShowSkillDropdown(true);
                        }}
                        onFocus={() => setShowSkillDropdown(true)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' && skillInput.trim()) {
                            e.preventDefault();
                            handleSelectSkill(skillInput.trim());
                          }
                        }}
                      />
                    </div>

                    {showSkillDropdown && skillInput.trim() !== "" && (
                      <div className="pj-skills-dropdown" style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        backgroundColor: "#ffffff",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                        zIndex: 50,
                        maxHeight: "200px",
                        overflowY: "auto",
                        marginTop: "4px"
                      }}>
                        {filteredSkillsPool.length > 0 ? (
                          filteredSkillsPool.map((pooledSkill) => {
                            const nameString = typeof pooledSkill === 'string' ? pooledSkill : pooledSkill.name || "";
                            return (
                              <div
                                key={nameString}
                                className="pj-dropdown-item"
                                style={{
                                  padding: "0.6rem 1rem",
                                  cursor: "pointer",
                                  borderBottom: "1px solid #f3f4f6",
                                  transition: "background-color 0.2s"
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = "#f3f4f6"}
                                onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
                                onClick={() => handleSelectSkill(nameString)}
                              >
                                ➕ {nameString}
                              </div>
                            );
                          })
                        ) : (
                          <div 
                            style={{ padding: "0.6rem 1rem", color: "#6b7280", fontSize: "0.875rem", cursor: "pointer" }}
                            onClick={() => handleSelectSkill(skillInput.trim())}
                          >
                            Press Enter to add custom skill: <strong>"{skillInput}"</strong>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Side Panel */}
          <div ref={statusPanelRef}>
            <div className="pj-preview-card" style={{ border: showReopenConfirm ? "2px solid #e11d48" : "1px solid #e5e7eb", transition: "all 0.2s ease" }}>
              <div className="pj-preview-title">Listing <em>Status</em></div>
              
              {!showReopenConfirm ? (
                <div style={{ textTransform: "uppercase", fontWeight: "bold", fontSize: "1.1rem", color: form.status?.toLowerCase() === "active" ? "#10b981" : "#f59e0b", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <span style={{ fontSize: "1.5rem" }}>●</span> {form.status}
                </div>
              ) : (
                <div style={{ background: "#fff5f5", padding: "1rem", borderRadius: "6px", border: "1px solid #fecdd3", marginTop: "0.5rem" }}>
                  <div style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start" }}>
                    <span style={{ fontSize: "1.2rem" }}>⚠️</span>
                    <div>
                      <h4 style={{ margin: "0 0 0.25rem 0", fontSize: "0.95rem", color: "#991b1b", fontWeight: "600" }}>Re-open This Listing?</h4>
                      <p style={{ margin: 0, fontSize: "0.825rem", color: "#7f1d1d", lineHeight: "1.4" }}>
                        Saving modifications on a <strong>Closed</strong> job will automatically re-publish it to active candidates.
                      </p>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem", justifyContent: "flex-end" }}>
                    <button 
                      type="button" 
                      className="emp-btn-ghost" 
                      style={{ padding: "0.35rem 0.75rem", fontSize: "0.8rem", background: "#fff" }}
                      onClick={() => setShowReopenConfirm(false)}
                      disabled={saving}
                    >
                      Cancel
                    </button>
                    <button 
                      type="button" 
                      className="emp-btn-primary" 
                      style={{ padding: "0.35rem 0.75rem", fontSize: "0.8rem", background: "#e11d48", color: "#fff" }}
                      onClick={() => executeSaveRequest("Active")}
                      disabled={saving}
                    >
                      {saving ? "Re-opening..." : "Confirm & Publish"}
                    </button>
                  </div>
                </div>
              )}

              <div className="pj-preview-divider" style={{ margin: "1rem 0" }} />
              
              {/* <div className="jd-side-metrics" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1rem" }}> */}
                {/* <div style={{ background: "#f9fafb", padding: "0.75rem", borderRadius: "6px", textAlign: "center" }}>
                  <div style={{ fontSize: "1.25rem", fontWeight: "bold", color: "#1f2937" }}>{metaStats.viewCount}</div>
                  <div style={{ fontSize: "0.75rem", color: "#6b7280" }}>Total Views</div>
                </div> */}
                <div style={{ background: "#f9fafb", padding: "0.75rem", borderRadius: "6px", textAlign: "center" }}>
                  <div style={{ fontSize: "1.25rem", fontWeight: "bold", color: "#1f2937" }}>{metaStats.applicationCount}</div>
                  <div style={{ fontSize: "0.75rem", color: "#6b7280" }}>Applications</div>
                </div>
              {/* </div> */}
            </div>
          </div>
        </div>
      ) : (
        /* Candidates Pipeline List Panel View */
        <div className="jd-applicants-container" style={{ marginTop: "1.5rem" }}>
          {applicants.length === 0 ? (
            <div className="ml-empty">
              <div className="ml-empty-icon">👥</div>
              <div className="ml-empty-title">No specialists have applied to this listing yet</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
              
              {/* Simplistic Elegant Pipeline Filter Chips */}
              <div className="jd-pipeline-filter" style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", borderBottom: "1px solid #f3f4f6", paddingBottom: "1rem" }}>
                {["All", "Under Review", "Shortlisted", "Interviewing", "Rejected"].map((opt) => {
                  const count = opt === "All" 
                    ? applicants.length 
                    : applicants.filter(a => {
                        const s = (a.currentStatus || "Under Review").replace(/\s+/g, '').toLowerCase();
                        if (opt === "Interviewing") return s === "interviewscheduled";
                        return s === opt.replace(/\s+/g, '').toLowerCase();
                      }).length;
                  
                  const isActive = filterStatus === opt;
                  
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setFilterStatus(opt)}
                      style={{
                        padding: "0.5rem 1rem",
                        borderRadius: "20px",
                        fontSize: "0.85rem",
                        fontWeight: "500",
                        cursor: "pointer",
                        border: "1px solid",
                        borderColor: isActive ? "#4f46e5" : "#e5e7eb",
                        backgroundColor: isActive ? "#eef2ff" : "#ffffff",
                        color: isActive ? "#4f46e5" : "#4b5563",
                        transition: "all 0.2s"
                      }}
                    >
                      {opt === "Under Review" ? "Pending" : opt === "Interviewing" ? "Interviews" : opt} ({count})
                    </button>
                  );
                })}
              </div>

              {/* Display List Container */}
              {displayedApplicants.length === 0 ? (
                <div className="ml-empty" style={{ padding: "3rem 1rem" }}>
                  <div className="ml-empty-icon">📂</div>
                  <div className="ml-empty-title" style={{ fontSize: "0.95rem", color: "#6b7280" }}>
                    No candidates are currently marked as "{filterStatus === "Under Review" ? "Pending" : filterStatus}"
                  </div>
                </div>
              ) : (
                <div className="jd-applicants-list" style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                  {displayedApplicants.map(a => {
                    // Match the applicant's current status string safely to config rules
                    const cleanStatus = (a.currentStatus || "UnderReview").replace(/\s+/g, '');
                    const matchedConfig = STATUS_OPTIONS.find(opt => opt.key.toLowerCase() === cleanStatus.toLowerCase()) || STATUS_OPTIONS[1];

                    return (
                      <div key={a.applicationId} className="jd-applicant-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#fff", padding: "1.25rem", borderRadius: "8px", border: "1px solid #f0f0f0" }}>
                        <div className="jd-applicant-identity" style={{ display: "flex", gap: "1rem", alignItems: "center", flex: 1 }}>
                          <div className="jd-avatar" style={{ width: "48px", height: "48px", borderRadius: "50%", background: "#fdf2f8", color: "#db2777", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold", fontSize: "1.1rem" }}>
                            {a.initials || (a.candidateName ? a.candidateName.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) : "SP")}
                          </div>
                          <div>
                            <div className="jd-app-name" style={{ fontWeight: "600", fontSize: "1.05rem", color: "#111827" }}>{a.candidateName}</div>
                            <div className="jd-app-meta" style={{ fontSize: "0.85rem", color: "#6b7280", marginTop: "0.15rem" }}>
                              Applied on: {a.appliedDate} · 📞 {a.phone || "No contact info"}
                            </div>
                          </div>
                        </div>

                        {/* Updated Actions Section: Interactive Select Dropped for Descriptive pill badges */}
                        <div className="jd-applicant-actions" style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
                          <span style={{
                            padding: "0.35rem 0.85rem",
                            borderRadius: "6px",
                            fontSize: "0.8rem",
                            fontWeight: "600",
                            letterSpacing: "0.025em",
                            color: matchedConfig.color,
                            backgroundColor: matchedConfig.bg,
                            border: `1px solid ${matchedConfig.color}20`,
                            whiteSpace: "nowrap"
                          }}>
                            {matchedConfig.label}
                          </span>
                          
                          <button 
                            type="button" 
                            className="emp-btn-ghost" 
                            style={{ padding: "0.35rem 0.85rem", fontSize: "0.875rem", whiteSpace: "nowrap" }}
                            onClick={() => navigate(`/employer/candidate/${a.applicationId}`)}
                          >
                            View Profile →
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </EmpLayout>
  );
}